import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, FileText, Youtube, Activity, Instagram, Download, Upload, ShieldCheck } from "lucide-react";
import { api } from "../../lib/api";
import logger from "../../lib/logger";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    events: 0,
  });
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const restoreInputRef = useRef(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const events = await api.getEvents();
        setStats({
          events: events.length,
        });
      } catch (err) {
        logger.warn("Dashboard stats fetch failed:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadBackup = async () => {
    setBackingUp(true);
    try {
      const response = await api.downloadBackup();
      const blob = new Blob([response.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      link.download = `demolition-backup-${stamp}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (err) {
      logger.error("Backup download failed:", err);
      toast.error("Backup failed - check console");
    } finally {
      setBackingUp(false);
    }
  };

  const handleRestoreFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // reset so the same file can be re-picked
    if (!file) return;

    if (!window.confirm(
      `Restore from "${file.name}"? This will OVERWRITE current events, page content, and photo records with matching IDs. Admin login is NOT affected.`
    )) {
      return;
    }

    setRestoring(true);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const result = await api.restoreBackup(payload);
      const r = result.restored || {};
      toast.success(`Restored ${r.events || 0} events, ${r.page_content || 0} content, ${r.photos || 0} photos`);
      // Refresh stats
      const events = await api.getEvents();
      setStats({ events: events.length });
    } catch (err) {
      logger.error("Restore failed:", err);
      toast.error(err?.response?.data?.detail || "Restore failed - invalid file?");
    } finally {
      setRestoring(false);
    }
  };

  const quickLinks = [
    { path: "/admin/events", label: "Manage Events", icon: Calendar, color: "bg-blue-600" },
    { path: "/admin/content", label: "Edit Content", icon: FileText, color: "bg-purple-600" },
    { path: "/admin/youtube", label: "YouTube Settings", icon: Youtube, color: "bg-red-600" },
  ];

  return (
    <div data-testid="admin-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-russo text-3xl text-white uppercase tracking-tighter">
          DASHBOARD
        </h1>
        <p className="font-mono text-white/50 mt-2">
          Welcome to the Ax and Smash Admin Panel
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        <div className="bg-demolition-paper border border-white/10 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-white/50 text-sm">Total Events</p>
              <p className="font-russo text-4xl text-white mt-2">
                {loading ? "-" : stats.events}
              </p>
            </div>
            <Calendar className="w-10 h-10 text-demolition-red opacity-50" />
          </div>
        </div>

        <div className="bg-demolition-paper border border-white/10 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-white/50 text-sm">Instagram Feed</p>
              <p className="font-teko text-2xl text-green-500 mt-2 uppercase">Connected</p>
            </div>
            <Instagram className="w-10 h-10 text-pink-500 opacity-50" />
          </div>
        </div>

        <div className="bg-demolition-paper border border-white/10 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-white/50 text-sm">YouTube API</p>
              <p className="font-teko text-2xl text-green-500 mt-2 uppercase">Active</p>
            </div>
            <Youtube className="w-10 h-10 text-red-500 opacity-50" />
          </div>
        </div>

        <div className="bg-demolition-paper border border-white/10 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-mono text-white/50 text-sm">Site Status</p>
              <p className="font-teko text-2xl text-green-500 mt-2 uppercase">Online</p>
            </div>
            <Activity className="w-10 h-10 text-green-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-12">
        <h2 className="font-teko text-xl text-demolition-chrome uppercase tracking-widest mb-6">
          QUICK ACTIONS
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {quickLinks.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.path}
                to={link.path}
                className="flex items-center gap-4 p-4 bg-demolition-paper border border-white/10 hover:border-demolition-red/50 transition-colors group"
                data-testid={`quick-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                <div className={`p-3 ${link.color}`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <span className="font-teko text-lg text-white uppercase tracking-wider group-hover:text-demolition-red transition-colors">
                  {link.label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Backup & Restore */}
      <div className="mb-12" data-testid="backup-restore-section">
        <h2 className="font-teko text-xl text-demolition-chrome uppercase tracking-widest mb-6">
          BACKUP &amp; RESTORE
        </h2>
        <div className="bg-demolition-paper border border-white/10 p-6 space-y-6">
          <div className="flex items-start gap-3 p-4 bg-demolition-subtle border border-yellow-500/30">
            <ShieldCheck className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
            <p className="font-mono text-white/70 text-sm">
              <strong className="text-yellow-500">Safeguard against deploy data loss:</strong>{" "}
              Download a backup before deploying a new version, then restore it on the live
              site afterwards. Admin login is always preserved from the environment and is
              never overwritten by a restore.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <button
              onClick={handleDownloadBackup}
              disabled={backingUp}
              className="flex items-center justify-center gap-3 px-6 py-4 bg-demolition-red text-white font-teko text-lg uppercase tracking-wider hover:bg-demolition-red-hover transition-colors disabled:opacity-50"
              data-testid="download-backup-btn"
            >
              <Download className="w-5 h-5" />
              {backingUp ? "Downloading..." : "Download Backup"}
            </button>

            <button
              onClick={() => restoreInputRef.current?.click()}
              disabled={restoring}
              className="flex items-center justify-center gap-3 px-6 py-4 border-2 border-demolition-red text-demolition-red font-teko text-lg uppercase tracking-wider hover:bg-demolition-red hover:text-white transition-colors disabled:opacity-50"
              data-testid="restore-backup-btn"
            >
              <Upload className="w-5 h-5" />
              {restoring ? "Restoring..." : "Restore from Backup"}
            </button>
            <input
              ref={restoreInputRef}
              type="file"
              accept=".json,application/json"
              onChange={handleRestoreFile}
              className="hidden"
              data-testid="restore-file-input"
            />
          </div>

          <p className="font-mono text-white/40 text-xs">
            Backup includes: events, page content, and photo records. Photo files
            themselves live in persistent object storage and survive deploys
            automatically.
          </p>
        </div>
      </div>

      {/* System Info */}
      <div>
        <h2 className="font-teko text-xl text-demolition-chrome uppercase tracking-widest mb-6">
          SYSTEM INFO
        </h2>
        <div className="bg-demolition-paper border border-white/10 p-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-white/10">
              <span className="font-mono text-white/50">Database</span>
              <span className="font-mono text-green-500">Connected</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-white/10">
              <span className="font-mono text-white/50">YouTube API</span>
              <span className="font-mono text-green-500">Active (30min cache)</span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-white/10">
              <span className="font-mono text-white/50">Instagram</span>
              <span className="font-mono text-green-500">@SearchandDestroyYou</span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="font-mono text-white/50">Last Updated</span>
              <span className="font-mono text-white/70">{new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
